import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpInterceptor, HttpEvent, HttpRequest, HttpHandler, HttpErrorResponse } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { EMPTY, Observable, throwError } from 'rxjs';
import { AuthTokenStorageService } from '../security/auth-token-storage.service';
import { AuthToken } from '../model/tokens/auth-token';
import { RefreshAuthToken } from '../model/tokens/refresh-auth-token';
import { JwtWrapperService } from '../security/jwt-wrapper.service';
import { LanguageService } from '../language/language.service';

/**
 * Interceptor qui enrichit toutes les requêtes HTTP sortantes avec les headers standards :
 * - Accept-Language : code langue de l'interface utilisateur (fr, en, es, it) pour les réponses du backend
 * - Authorization : token JWT d'authentification (si l'utilisateur est connecté)
 */
@Injectable()
export class RequestInterceptor implements HttpInterceptor {
  constructor(
    private jwtWrapperService: JwtWrapperService,
    private authTokenStorageService: AuthTokenStorageService,
    private languageService: LanguageService,
    private router: Router,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Ajouter le header Accept-Language avec le code langue de l'interface
    const currentLanguage = this.languageService.getCurrentLanguage();
    req = req.clone({
      headers: req.headers.set('Accept-Language', currentLanguage),
    });

    // Ajouter le header Authorization Bearer si l'utilisateur est authentifié
    const authToken = this.authTokenStorageService.getToken();
    if (authToken && this.jwtWrapperService.isLoggedIn()) {
      req = this.setupBearerHeader(req, authToken);
    }

    return this.returnHandler(req, next);
  }

  private returnHandler(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((x) => {
        return this.handleAuthError(x);
      }),
    );
  }

  /**
   * Toute réponse 401 du serveur signifie que la session n'est plus valide (JWT expiré,
   * absent ou invalide) : on déconnecte proprement et on redirige vers /login en conservant
   * l'URL courante (pour y revenir après reconnexion), plutôt que de laisser une page cassée.
   *
   * Exceptions : les 401 de l'endpoint d'authentification (mauvais identifiants), gérés
   * localement par les pages login/access, et le cas où l'on est déjà sur /login.
   */
  private handleAuthError(err: HttpErrorResponse): Observable<any> {
    const isAuthEndpoint = err.url?.endsWith('/authenticate') ?? false;
    if (err.status === 401 && !isAuthEndpoint && !this.router.url.startsWith('/login')) {
      this.authTokenStorageService.logout();
      this.router.navigate(['/login'], { queryParams: { redirectUrl: this.router.url } });
      return EMPTY;
    }
    return throwError(() => err);
  }

  /**
   * Ajoute le header Authorization Bearer dans les headers de la requête
   * @param request - Requête HTTP à enrichir
   * @param authToken - Token JWT d'authentification
   * @returns request - Requête avec le header Authorization ajouté
   */
  private setupBearerHeader(request: HttpRequest<any>, authToken: AuthToken | RefreshAuthToken): HttpRequest<any> {
    const cloned = request.clone({
      headers: request.headers.set('Authorization', 'Bearer ' + authToken.token),
    });
    return cloned;
  }
}
